import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, InputAdornment,
  IconButton, Typography, CircularProgress, Snackbar, Alert, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { Api, MediaAsset } from './api';
import { Pager } from './Pager';

const fmtSize = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

// The reusable asset grid: search, upload, and either select (picker mode) or
// copy/delete (library mode).
export function MediaBrowser({
  api, tenantId, canUpload, canDelete, onSelect,
}: {
  api: Api; tenantId: string; canUpload: boolean; canDelete: boolean;
  onSelect?: (url: string) => void;
}) {
  const [items, setItems] = useState<MediaAsset[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Fetch one page of the library. Page navigation calls this directly (no
  // debounce); typing goes through the debounced effect below. `total` is kept
  // across loads so the pagination control doesn't flash out while fetching.
  const fetchPage = useCallback((query: string, p: number) => {
    setItems(null);
    setPage(p);
    api.media(tenantId, query, p, pageSize)
      .then((res) => { setItems(res.items); setTotal(res.total); })
      .catch((e) => setToast({ msg: e.message, sev: 'error' }));
  }, [api, tenantId, pageSize]);

  // Initial load, tenant switch, debounced search, and page-size change all land
  // here; any of those resets to the first page.
  useEffect(() => {
    const t = setTimeout(() => fetchPage(q.trim(), 1), 250);
    return () => clearTimeout(t);
  }, [q, fetchPage]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const uploaded: MediaAsset[] = [];
      for (const f of Array.from(files)) uploaded.push(await api.uploadMedia(tenantId, f));
      // Newest sort first, so jump to page 1 to reveal what was just added.
      fetchPage(q.trim(), 1);
      setToast({ msg: `Uploaded ${uploaded.length} file${uploaded.length > 1 ? 's' : ''}`, sev: 'success' });
    } catch (e: any) {
      setToast({ msg: e.message === 'too_large' ? 'File too large (max 10MB).' : e.message === 'unsupported_type' ? 'Unsupported file type.' : `Upload failed: ${e.message}`, sev: 'error' });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (id: string) => {
    try {
      await api.deleteMedia(tenantId, id);
      // Reload the current page; if we just emptied a trailing page, step back one.
      const lastOnPage = (items?.length ?? 0) <= 1 && page > 1;
      fetchPage(q.trim(), lastOnPage ? page - 1 : page);
    } catch (e: any) { setToast({ msg: e.message, sev: 'error' }); }
  };
  const copy = (url: string) => { navigator.clipboard?.writeText(url); setToast({ msg: 'URL copied', sev: 'success' }); };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField
          size="small" fullWidth placeholder="Search files…" value={q} onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {canUpload ? (
          <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" multiple hidden onChange={(e) => upload(e.target.files)} />
            <Button variant="contained" startIcon={<UploadIcon />} disabled={busy} onClick={() => fileRef.current?.click()} sx={{ flexShrink: 0 }}>
              {busy ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        ) : null}
      </Box>

      {!items ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          <UploadIcon sx={{ fontSize: 40, opacity: 0.35 }} />
          <Typography sx={{ mt: 1 }}>
            {q.trim() ? 'No images match your search.' : `No images yet.${canUpload ? ' Upload one to get started.' : ''}`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
          {items.map((m) => (
            <Box key={m.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', position: 'relative', '&:hover .ov': { opacity: 1 } }}>
              <Box
                onClick={() => (onSelect ? onSelect(m.url) : copy(m.url))}
                sx={{ height: 110, bgcolor: 'grey.100', display: 'grid', placeItems: 'center', cursor: 'pointer', backgroundImage: `url("${m.url}")`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
              />
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600 }} title={m.filename}>{m.filename}</Typography>
                <Typography variant="caption" color="text.secondary">{m.width && m.height ? `${m.width}×${m.height} · ` : ''}{fmtSize(m.size)}</Typography>
              </Box>
              <Box className="ov" sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity .15s' }}>
                {onSelect ? (
                  <Tooltip title="Use this image"><IconButton size="small" sx={{ bgcolor: 'background.paper' }} onClick={() => onSelect(m.url)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
                ) : (
                  <Tooltip title="Copy URL"><IconButton size="small" sx={{ bgcolor: 'background.paper' }} onClick={() => copy(m.url)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
                )}
                {canDelete ? (
                  <Tooltip title="Delete"><IconButton size="small" color="error" sx={{ bgcolor: 'background.paper' }} onClick={() => remove(m.id)}><DeleteOutlineIcon fontSize="inherit" /></IconButton></Tooltip>
                ) : null}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {total > 0 ? (
        <Pager
          page={page} pageCount={pageCount} pageSize={pageSize} total={total}
          onPage={(p) => fetchPage(q.trim(), p)} onPageSize={setPageSize}
        />
      ) : null}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

// Full-page library (nav "Media").
export function MediaLibrary({ api, tenantId, canUpload, canDelete }: { api: Api; tenantId: string; canUpload: boolean; canDelete: boolean }) {
  return <Box sx={{ maxWidth: 1100 }}><MediaBrowser api={api} tenantId={tenantId} canUpload={canUpload} canDelete={canDelete} /></Box>;
}

// --- Picker context: any field (e.g. PropForm image inputs) can request an image ---
interface PickerCtx { pick: () => Promise<string | null> }
const Ctx = createContext<PickerCtx | null>(null);
export const useImagePicker = () => useContext(Ctx);

export function MediaPickerProvider({
  api, tenantId, canUpload, canDelete, children,
}: {
  api: Api; tenantId: string; canUpload: boolean; canDelete: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const resolver = useRef<((v: string | null) => void) | null>(null);
  const pick = useCallback(() => new Promise<string | null>((res) => { resolver.current = res; setOpen(true); }), []);
  const finish = (url: string | null) => { setOpen(false); resolver.current?.(url); resolver.current = null; };

  return (
    <Ctx.Provider value={{ pick }}>
      {children}
      <Dialog open={open} onClose={() => finish(null)} maxWidth="md" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
        <DialogTitle>Choose an image</DialogTitle>
        <DialogContent dividers>
          <MediaBrowser api={api} tenantId={tenantId} canUpload={canUpload} canDelete={canDelete} onSelect={(url) => finish(url)} />
        </DialogContent>
        <DialogActions><Button onClick={() => finish(null)}>Cancel</Button></DialogActions>
      </Dialog>
    </Ctx.Provider>
  );
}
